import { Box, Skeleton, Stack, Typography } from "@mui/material";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { GeneratorStatusReviewExplanationBasis } from "generated-graphql/graphql";
import { DateTime } from "luxon";

import { DrawerDisclosure } from "./DrawerDisclosure";
import { chronologicalReviewEvents, ReviewEvent } from "./reviewHistory";

function activityLabel(eventType: string): string {
  if (eventType === "OPENED") return "Flagged for review";
  if (eventType === "AUTO_RESOLVED") return "Automatically marked resolved by the system";
  if (eventType === "REOPENED") return "Reopened for review";
  if (eventType === "RESOLVED") return "Marked resolved";
  if (eventType === "IGNORED") return "Dismissed";
  if (eventType === "UNVALIDATED") return "Validation undone";
  if (eventType === "PENDING") return "Moved to follow-up";
  if (eventType === "LINKED_CORRECTIONS_CHANGED") return "Linked corrections changed";
  return prettyPrintEnumValue(eventType);
}

/** This event's note is the system's list of added and removed corrections, not a reason. */
function noteLabel(eventType: string): string {
  return eventType === "LINKED_CORRECTIONS_CHANGED" ? "" : "Recorded reason: ";
}

function Activity({ event }: { event: ReviewEvent }) {
  const occurredAt = DateTime.fromISO(event.createdAt, { zone: "utc" }).toLocal();
  const context =
    event.explanation?.basis === GeneratorStatusReviewExplanationBasis.HistoricalComparison
      ? event.explanation.message
      : null;
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "160px minmax(0, 1fr)" },
        gap: { xs: 0.5, sm: 2 },
      }}
    >
      <Box component="time" dateTime={event.createdAt}>
        <Typography variant="body2" color="text.secondary">
          {occurredAt.toLocaleString(DateTime.DATE_MED)}
        </Typography>
        <Typography variant="caption" color="text.secondary" component="div">
          {occurredAt.toLocaleString(DateTime.TIME_SIMPLE)}
          {event.actorName ? ` · ${event.actorName}` : ""}
        </Typography>
      </Box>
      <Box>
        <Typography variant="body2" fontWeight={600}>
          {activityLabel(event.eventType)}
        </Typography>
        {context && (
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            {context}
          </Typography>
        )}
        {event.comparison && (
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            {event.comparison}
          </Typography>
        )}
        {event.note ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {noteLabel(event.eventType)}
            {event.note}
          </Typography>
        ) : (
          !context &&
          (event.eventType === "REOPENED" || event.eventType === "AUTO_RESOLVED") && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              No reason was recorded for this change.
            </Typography>
          )
        )}
      </Box>
    </Box>
  );
}

/** The complete audit trail supports the current review status. */
export function FindingHistory({
  events,
  loading,
}: {
  events?: readonly ReviewEvent[];
  loading?: boolean;
}) {
  const chronological = chronologicalReviewEvents(events ?? []);
  return (
    <Box>
      {loading && !events ? (
        <Skeleton height={40} />
      ) : (
        <DrawerDisclosure title={`Review history (${chronological.length})`}>
          {chronological.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              No review history recorded.
            </Typography>
          )}
          <Stack component="ol" spacing={2} sx={{ m: 0, p: 1.5, listStyle: "none" }}>
            {chronological.map((event) => (
              <Box component="li" key={event.id}>
                <Activity event={event} />
              </Box>
            ))}
          </Stack>
        </DrawerDisclosure>
      )}
    </Box>
  );
}
